/**
 * The client-side FIFO queue for `/fetch`, and the poll loop behind it.
 *
 * §11.7 of `docs/plans/manuscript-file-delivery.md`. THIS IS NOT A POLISH ITEM.
 * The backend allows a user `download_user_concurrent = 2` jobs at once and
 * answers 429 beyond that; without a queue, ten quick clicks (or one gallery
 * scroll, if auto-preview is ever wired) fire ten POSTs and take eight 429s,
 * and eight broken cards are worse than eight that say "tap to load". With the
 * queue in place a 429 means *a bug in this file*, not normal operation —
 * which is what makes it worth alerting on server-side.
 *
 * ONE MODULE-SCOPE SINGLETON, deliberately, not a React hook: the limit is
 * per USER, not per component tree, and two bubbles that mount and unmount as
 * the reviewer scrolls must not each get their own budget. Components subscribe
 * (`useMediaTask`); they do not own the state.
 *
 * The poll loop lives here for the same reason. `GET /fetch` IS the abandonment
 * heartbeat — it writes `last_polled_at`, and the worker abandons a job nobody
 * has polled for `download_abandon_sec` (180 s). So a download survives the
 * reviewer collapsing the card or scrolling away, and dies when they close the
 * tab. That is the intended behaviour on both sides, and it is a property of
 * where this loop lives.
 */

import {
  MediaFetchError,
  MediaFetchReason,
  cancelMediaFetch,
  getMediaFetchStatus,
  parseMediaFetchError,
  requestMediaFetch,
} from '@/api/media';

/**
 * `bot_config` `manuscript.download_user_concurrent`, seeded 2 by migration 073
 * and read live as 2 on 2026-08-22.
 *
 * HARDCODED, AND THAT IS A KNOWN GAP: no route serves `bot_config` to the SPA
 * (the backend reads this key in `POST /fetch` only), and Phase 4 adds no
 * backend routes. If the operator raises the row without changing this
 * constant, the client simply queues more conservatively than the server
 * allows — the safe direction. Lowering the row without changing this constant
 * is the unsafe direction and would show up as 429s.
 */
export const DOWNLOAD_USER_CONCURRENT = 2;

/**
 * §11.7's reservation: automatic work never fills the last slot, so a
 * reviewer's deliberate click is never queued behind their own scrolling.
 */
const AUTO_IN_FLIGHT_LIMIT = DOWNLOAD_USER_CONCURRENT - 1;

/**
 * `bot_config` `manuscript.preview_auto_max_bytes`, seeded 2097152 by migration
 * 072 — "SPA auto-preview threshold, served to the client" in the plan's §8
 * table. It is NOT in fact served to the client (no route reads it), so it is
 * mirrored here; same known gap as `DOWNLOAD_USER_CONCURRENT` above.
 */
export const PREVIEW_AUTO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * ~1.5 s, per §5.1. Two active jobs is the ceiling, so this is at most ~1.3
 * requests/second from one reviewer, each answered by one `os.path.exists` and
 * one indexed query.
 */
const POLL_INTERVAL_MS = 1500;

/**
 * Which lane a request came from. `manual` is a human clicking; `auto` is
 * anything the UI decided on its own. Only `manual` may use the last slot.
 */
export type MediaFetchLane = 'auto' | 'manual';

export type MediaTaskState =
  /** Nobody has asked anything about this file yet. */
  | 'unknown'
  /** A status GET is in flight. Costs no Telegram work and takes no slot. */
  | 'probing'
  /** In this queue, holding no server resource: the POST has not been sent. */
  | 'waiting'
  /** Server states, verbatim from the wire. */
  | 'queued' | 'running' | 'ready' | 'unavailable'
  /** An HTTP or transport failure that is not one of the wire `reason`s. */
  | 'error';

export interface MediaTask {
  state: MediaTaskState;
  /** ⚠ A checkpoint snapshot (8 MiB steps), NOT a live byte counter. */
  loaded: number | null;
  total: number | null;
  reason: MediaFetchReason | null;
  /** True when this reviewer joined a job another reviewer had already created. */
  joined: boolean;
  jobId: number | null;
  error: MediaFetchError | null;
}

const EMPTY_TASK: MediaTask = Object.freeze({
  state: 'unknown' as MediaTaskState,
  loaded: null,
  total: null,
  reason: null,
  joined: false,
  jobId: null,
  error: null,
});

interface ActiveEntry {
  lane: MediaFetchLane;
  timer: number | undefined;
  /**
   * Whether this task counts against `download_user_concurrent`.
   *
   * TRUE for anything this page POSTed. FALSE for a job discovered by
   * `probeMediaFetch` — the server's concurrent limit counts `scraper_jobs`
   * rows with `created_by = <this user>`, so watching a job ANOTHER reviewer
   * created costs this user nothing and must not block their own request.
   * Getting this wrong is not a leak but a lockout: a reviewer with two
   * colleagues' downloads on screen could not start one of their own.
   */
  holdsSlot: boolean;
}

const tasks = new Map<number, MediaTask>();
const listeners = new Map<number, Set<() => void>>();

/** FIFO. Manual entries are inserted ahead of auto ones; see `enqueue`. */
const waiting: Array<{ messageId: number; lane: MediaFetchLane }> = [];

/**
 * Everything holding (or about to hold) a server-side slot: from the moment a
 * task is admitted until it reaches a terminal state. The POST window counts —
 * a task whose POST is in flight has already spent its admission.
 */
const active = new Map<number, ActiveEntry>();

// ---------------------------------------------------------------------------
// Store plumbing
// ---------------------------------------------------------------------------

export const getMediaTask = (messageId: number): MediaTask =>
  tasks.get(messageId) ?? EMPTY_TASK;

export const subscribeMediaTask = (messageId: number, listener: () => void): (() => void) => {
  let set = listeners.get(messageId);
  if (!set) {
    set = new Set();
    listeners.set(messageId, set);
  }
  set.add(listener);
  return () => {
    set!.delete(listener);
    if (set!.size === 0) listeners.delete(messageId);
  };
};

/**
 * Replaces the frozen task object rather than mutating it — `useSyncExternalStore`
 * compares snapshots by identity, so an in-place edit renders nothing.
 */
const setTask = (messageId: number, patch: Partial<MediaTask>): void => {
  const next = Object.freeze({ ...getMediaTask(messageId), ...patch });
  tasks.set(messageId, next);
  const set = listeners.get(messageId);
  if (set) set.forEach((listener) => listener());
};

// ---------------------------------------------------------------------------
// Admission
// ---------------------------------------------------------------------------

const slotCount = (lane?: MediaFetchLane): number => {
  let count = 0;
  active.forEach((entry) => {
    if (!entry.holdsSlot) return;
    if (lane && entry.lane !== lane) return;
    count += 1;
  });
  return count;
};

const admissible = (lane: MediaFetchLane): boolean => {
  if (slotCount() >= DOWNLOAD_USER_CONCURRENT) return false;
  if (lane === 'auto' && slotCount('auto') >= AUTO_IN_FLIGHT_LIMIT) return false;
  return true;
};

/**
 * Admit as much as the limits allow, oldest first within a lane.
 *
 * Scans rather than shifting blindly: the head of the queue may be an `auto`
 * entry that cannot run while a `manual` one behind it can, and a strict
 * shift would then hold the reserved slot empty — the opposite of what the
 * reservation is for.
 */
const pump = (): void => {
  for (let i = 0; i < waiting.length; ) {
    const next = waiting[i];
    if (!admissible(next.lane)) {
      i += 1;
      continue;
    }
    waiting.splice(i, 1);
    active.set(next.messageId, { lane: next.lane, timer: undefined, holdsSlot: true });
    void runTask(next.messageId);
    // No `i` advance: the array shifted under us, and the next candidate is
    // now at this index.
  }
};

const finish = (messageId: number): void => {
  const entry = active.get(messageId);
  if (entry) {
    window.clearTimeout(entry.timer);
    active.delete(messageId);
  }
  pump();
};

// ---------------------------------------------------------------------------
// The task itself
// ---------------------------------------------------------------------------

const failFrom = (messageId: number, error: unknown): void => {
  const parsed = parseMediaFetchError(error);
  // `reason` is only meaningful on the `unavailable` shape. A 429 carries
  // `reason: "daily"` / `"concurrent"`, which are NOT members of
  // `MediaFetchReason` and must not be laundered into it — that body is read
  // through `error` instead.
  const unavailable = !!parsed && parsed.state === 'unavailable';
  setTask(messageId, {
    state: unavailable ? 'unavailable' : 'error',
    reason: unavailable ? (parsed!.reason as MediaFetchReason) : null,
    error: parsed,
  });
};

const runTask = async (messageId: number): Promise<void> => {
  try {
    const response = await requestMediaFetch(messageId);
    if (!active.has(messageId)) return;  // cancelled while the POST was in flight

    if (response.state === 'ready') {
      // A cache hit: nothing was enqueued, nothing counted, and the slot is
      // released immediately.
      setTask(messageId, {
        state: 'ready', reason: null, error: null,
        joined: response.joined, jobId: response.job_id,
      });
      finish(messageId);
      return;
    }

    setTask(messageId, {
      state: response.state, reason: null, error: null,
      joined: response.joined, jobId: response.job_id,
    });
    schedulePoll(messageId, 0);
  } catch (error) {
    if (!active.has(messageId)) return;
    failFrom(messageId, error);
    finish(messageId);
  }
};

const schedulePoll = (messageId: number, delay = POLL_INTERVAL_MS): void => {
  const entry = active.get(messageId);
  if (!entry) return;
  window.clearTimeout(entry.timer);
  entry.timer = window.setTimeout(() => { void pollOnce(messageId); }, delay);
};

const pollOnce = async (messageId: number): Promise<void> => {
  if (!active.has(messageId)) return;
  try {
    const status = await getMediaFetchStatus(messageId);
    if (!active.has(messageId)) return;  // cancelled while the GET was in flight

    if (status.state === 'ready') {
      setTask(messageId, {
        state: 'ready', loaded: status.loaded, total: status.total,
        reason: null, error: null, jobId: null,
      });
      finish(messageId);
      return;
    }
    if (status.state === 'unavailable') {
      setTask(messageId, {
        state: 'unavailable', reason: status.reason, error: null,
        jobId: status.job_id,
      });
      finish(messageId);
      return;
    }
    setTask(messageId, {
      state: status.state, loaded: status.loaded, total: status.total,
      reason: null, error: null, jobId: status.job_id,
    });
    schedulePoll(messageId);
  } catch (error) {
    if (!active.has(messageId)) return;
    failFrom(messageId, error);
    finish(messageId);
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Ask the server whether this file is already on disk, WITHOUT enqueueing
 * anything.
 *
 * Costs no Telegram work and takes no queue slot, so an expanded card can ask
 * freely. If it finds a job already in flight — this reviewer's, or another's,
 * since two reviewers share one row — it starts following it, and following
 * DOES take a slot, because that job is occupying one of this user's two
 * server-side slots whether or not this page created it.
 */
export const probeMediaFetch = async (messageId: number): Promise<void> => {
  if (active.has(messageId)) return;
  const existing = getMediaTask(messageId);
  if (existing.state === 'probing' || existing.state === 'waiting') return;

  setTask(messageId, { state: 'probing', error: null });
  try {
    const status = await getMediaFetchStatus(messageId);
    if (status.state === 'ready') {
      setTask(messageId, {
        state: 'ready', loaded: status.loaded, total: status.total,
        reason: null, error: null, jobId: null,
      });
      return;
    }
    if (status.state === 'unavailable') {
      setTask(messageId, {
        state: 'unavailable', reason: status.reason, error: null,
        jobId: status.job_id,
      });
      return;
    }
    setTask(messageId, {
      state: status.state, loaded: status.loaded, total: status.total,
      reason: null, error: null, jobId: status.job_id,
    });
    if (!active.has(messageId)) {
      // Follow it without taking a slot — see `ActiveEntry.holdsSlot`. Polling
      // it is also the heartbeat that keeps a shared job alive, which is
      // correct: this page IS watching it.
      active.set(messageId, { lane: 'manual', timer: undefined, holdsSlot: false });
      schedulePoll(messageId);
    }
  } catch (error) {
    failFrom(messageId, error);
  }
};

/**
 * Ask for the file. Idempotent: a second call while one is in flight, waiting
 * or already `ready` does nothing, which is what makes a double-click safe.
 */
export const requestMedia = (messageId: number, lane: MediaFetchLane = 'manual'): void => {
  if (active.has(messageId)) return;
  if (waiting.some((entry) => entry.messageId === messageId)) return;
  const existing = getMediaTask(messageId);
  if (existing.state === 'ready') return;

  setTask(messageId, { state: 'waiting', reason: null, error: null });
  // Manual work goes ahead of queued auto work — a human waiting on a click
  // must not sit behind previews the UI decided to fetch on its own.
  if (lane === 'manual') {
    const firstAuto = waiting.findIndex((entry) => entry.lane === 'auto');
    if (firstAuto >= 0) waiting.splice(firstAuto, 0, { messageId, lane });
    else waiting.push({ messageId, lane });
  } else {
    waiting.push({ messageId, lane });
  }
  pump();
};

/**
 * Stop this file's fetch.
 *
 * A task still in the client queue is simply dropped — nothing was ever sent,
 * so there is nothing to cancel server-side. An active one gets the DELETE, and
 * the worker stops at its next progress checkpoint.
 *
 * The state goes to `unavailable`/`canceled` locally without waiting for a
 * poll: the DELETE is a 204 whether or not it matched anything (a joiner may
 * not kill the creator's job, and is answered 204 anyway so the response cannot
 * be used to probe for the row's existence), so the local state is the honest
 * one — "this page is no longer waiting for it".
 */
export const cancelMedia = async (messageId: number): Promise<void> => {
  const queuedIndex = waiting.findIndex((entry) => entry.messageId === messageId);
  if (queuedIndex >= 0) {
    waiting.splice(queuedIndex, 1);
    setTask(messageId, { state: 'unknown', reason: null, error: null });
    return;
  }
  if (!active.has(messageId)) return;

  finish(messageId);
  setTask(messageId, { state: 'unavailable', reason: 'canceled', error: null });
  try {
    await cancelMediaFetch(messageId);
  } catch (error) {
    // The local state is already "not waiting"; a failed DELETE means the
    // worker may still be transferring, and the abandonment heartbeat then
    // stops it within `download_abandon_sec`.
    console.warn('media fetch cancel failed', error);
  }
};

/** Test/diagnostic view of the limiter. Not used by the UI. */
export const mediaQueueDepth = (): { slots: number; following: number; waiting: number } =>
  ({ slots: slotCount(), following: active.size - slotCount(), waiting: waiting.length });
